'use client';

import Link from 'next/link';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Button } from '@mui/material';

// Deep import, not the '@/components' barrel: this module is in the always-rendered
// graph, where a barrel import pulls the whole component tree (charts included).
import { MoreDropdown } from '@/components/specific/nav/MoreDropdown/MoreDropdown';
import { capitalizeWords } from '@/utils';

import { PrimaryFilter } from '@/constants';

type PillNavItemProps = {
  item: PrimaryFilter;
  isActive: boolean;
  isMoreOpen: boolean;
  moreButtonRef: React.RefObject<HTMLButtonElement>;
  onMoreClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMoreClose: () => void;
};

export const PillNavItem = ({
  item,
  isActive,
  isMoreOpen,
  moreButtonRef,
  onMoreClick,
  onMoreClose,
}: PillNavItemProps) => {
  const baseStyles = {
    px: 1,
    py: 1.25,
    minWidth: 'auto',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'none' as const,
    color: isActive ? 'primary.main' : 'text.primary',
    borderRadius: 0,
    borderBottom: '3px solid',
    borderBottomColor: isActive ? 'primary.main' : 'transparent',
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'primary.main',
    },
  };

  if (item.isMore) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          flexDirection: 'column',
          zIndex: isMoreOpen ? 10000 : 'auto',
          overflow: 'visible',
        }}
      >
        <Button
          ref={moreButtonRef}
          onClick={onMoreClick}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                transition: 'transform 0.2s',
                transform: isMoreOpen ? 'rotate(180deg)' : 'rotate(0)',
                fontSize: '1rem',
              }}
            />
          }
          sx={{
            ...baseStyles,
            color: isMoreOpen ? 'primary.main' : 'text.primary',
            borderBottomColor: isMoreOpen ? 'primary.main' : 'transparent',
          }}
        >
          {capitalizeWords(item.label)}
        </Button>
        {isMoreOpen && (
          <MoreDropdown isOpen={isMoreOpen} onClose={onMoreClose} triggerRef={moreButtonRef} />
        )}
      </Box>
    );
  }

  return (
    <Link href={item.path} style={{ textDecoration: 'none', display: 'flex' }} prefetch={true}>
      <Box
        sx={{
          ...baseStyles,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
        }}
      >
        {item.label === 'Trending' && (
          <TrendingUpIcon
            sx={{
              fontSize: '1rem',
              color: isActive ? 'primary.main' : 'text.primary',
            }}
          />
        )}
        {capitalizeWords(item?.label)}
      </Box>
    </Link>
  );
};

export default PillNavItem;
